import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Flex,
  Text,
  Box,
  Divider,
  useToast,
  Icon,
  useColorModeValue,
  VStack,
  Heading,
} from "@chakra-ui/react";
import { IoSparkles } from "react-icons/io5";
import { useCallback, useState, useEffect } from "react";
import { Post } from "api";
import { useDispatch } from "react-redux";
import { showGlobalLoading, hideGlobalLoading } from "helper/GlobalLoadingDispatches";
import { useConfirmDialog } from "components/confirmModal";
import { AllSuggestionsModalProps, CampaignGroup, GroupedSuggestions, SuggestionProps } from "./types";
import SuggestionComparisonView from "./SuggestionComparisonView";

const AllSuggestionsModal = ({
  isOpen,
  onClose,
  suggestions,
  onApplySuccess,
}: AllSuggestionsModalProps) => {
  const [isApplying, setIsApplying] = useState(false);
  const [appliedSuggestions, setAppliedSuggestions] = useState<number[]>([]);
  const [failedSuggestions, setFailedSuggestions] = useState<number[]>([]);
  const toast = useToast();
  const dispatch = useDispatch();
  const textColor = useColorModeValue("secondaryGray.900", "white");
  const bgColor = useColorModeValue("gray.50", "navy.700");

  const { ConfirmDialog, openDialog } = useConfirmDialog({
    confirmButtonText: "Apply All",
    centerButtons: true,
    hideCancelButton: true
  });

  useEffect(() => {
    if (isOpen) {
      setAppliedSuggestions([]);
      setFailedSuggestions([]);
      setIsApplying(false);
    }
  }, [isOpen, suggestions]);

  // Group suggestions by type first (CPI/Budget), then by campaign
  const groupedBySuggestionType = suggestions.reduce(
    (acc: { [key: string]: { type: string; campaigns: { [key: string]: SuggestionProps[] } } }, suggestion) => {
      const type = suggestion.type === "bid" ? "CPI Suggestions" : "Cap Suggestions";
      
      if (!acc[type]) {
        acc[type] = {
          type,
          campaigns: {},
        };
      }
      
      const campaignId = suggestion.campaign.id;
      if (!acc[type].campaigns[campaignId]) {
        acc[type].campaigns[campaignId] = [];
      }
      
      acc[type].campaigns[campaignId].push(suggestion);
      return acc;
    },
    {}
  );

  const handleApplyAllSuggestions = useCallback(async () => {
    if (suggestions.length === 0) return;

    const result = await openDialog({
      title: `Apply ${suggestions.length} suggestions to optimize your campaigns`,
      content: (
        <Box mt={4}>
          <Text mb={4}>
            You are about to apply the following suggestions:
          </Text>
          <VStack spacing={4} align="stretch" maxH="300px" overflowY="auto">
            {Object.entries(groupedBySuggestionType).map(([type, group]) => (
              <Box key={type}>
                <Heading size="sm" mb={2}>{type}</Heading>
                <VStack spacing={2} align="stretch">
                  {Object.entries(group.campaigns).map(([campaignId, campaignSuggestions]) => (
                    <Box 
                      key={campaignId}
                      bg={bgColor}
                      p={2}
                      borderRadius="md"
                    >
                      <Text fontWeight="bold" fontSize="sm" mb={1}>
                        {campaignSuggestions[0].campaign.name}
                      </Text>
                      {campaignSuggestions.map((suggestion) => (
                        <SuggestionComparisonView
                          key={suggestion.id}
                          {...suggestion}
                        />
                      ))}
                    </Box>
                  ))}
                </VStack>
              </Box>
            ))}
          </VStack>
        </Box>
      ),
    });

    if (!result) return;

    try {
      setIsApplying(true);
      showGlobalLoading("Applying suggestions...");

      const results = await Promise.allSettled(
        suggestions.map((suggestion) =>
          Post(`/dashboard/campaign-suggestions/${suggestion.id}/apply`, {})
            .then(() => suggestion.id)
        )
      );

      const applied = results
        .filter((r) => r.status === "fulfilled")
        .map((r) => r.status === "fulfilled" ? r.value : -1) as number[];
      
      const failed = results
        .filter((r) => r.status === "rejected")
        .map((_, index) => suggestions[index].id);

      setAppliedSuggestions(applied);
      setFailedSuggestions(failed);

      toast({
        title: `${applied.length} of ${suggestions.length} suggestions applied`,
        status: applied.length > 0 ? "success" : "error",
        duration: 5000,
        isClosable: true,
      });

      if (onApplySuccess && applied.length > 0) {
        onApplySuccess(applied);
      }

      if (applied.length === suggestions.length) {
        setTimeout(() => {
          onClose();
        }, 1500);
      }
    } catch (error) {
      console.error("Error applying suggestions:", error);
      toast({
        title: "Error",
        description: "Failed to apply suggestions",
        status: "error",
        duration: 5000,
      });
    } finally {
      hideGlobalLoading();
      setIsApplying(false);
    }
  }, [suggestions, toast, onClose, openDialog, groupedBySuggestionType, onApplySuccess, bgColor]);

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Flex alignItems="center">
              <Icon as={IoSparkles} color="brand.500" mr={2} />
              <Text color={textColor}>Campaign Improvements Available</Text>
            </Flex>
          </ModalHeader>
          <ModalCloseButton />
          
          <ModalBody>
            {suggestions.length === 0 ? (
              <Flex justifyContent="center" alignItems="center" height="200px" direction="column">
                <Text fontSize="lg" mb={4}>No suggestions available</Text>
                <Button onClick={onClose}>Close</Button>
              </Flex>
            ) : (
              <>
                <Flex mb={3}>
                  <Text>
                    {suggestions.length} improvements for{" "}
                    {Object.values(groupedBySuggestionType).reduce(
                      (total, group) => total + Object.keys(group.campaigns).length, 0
                    )} campaigns
                  </Text>
                </Flex>
                
                <Divider mb={3} />
                
                {isApplying && (
                  <Flex justify="center" align="center" direction="column" py={6}>
                    <Text fontSize="lg" mb={4}>
                      Applying {suggestions.length} suggestions...
                    </Text>
                  </Flex>
                )}
                
                {appliedSuggestions.length > 0 && (
                  <Flex direction="column" mb={4}>
                    <Text fontSize="lg" fontWeight="bold" mb={2} color="green.500">
                      {appliedSuggestions.length} suggestions applied successfully
                    </Text>
                    {failedSuggestions.length > 0 && (
                      <Text fontSize="md" color="red.500">
                        {failedSuggestions.length} suggestions failed to apply
                      </Text>
                    )}
                  </Flex>
                )}
                
                {!isApplying && (
                  <VStack spacing={4} align="stretch" maxH="600px" overflowY="auto">
                    {Object.entries(groupedBySuggestionType).map(([type, group]) => (
                      <Box key={type} mb={4}>
                        <Heading size="sm" mb={2}>{type}</Heading>
                        
                        {type === "CPI Suggestions" && (
                          <Text fontSize="sm" color="gray.600" mb={3}>
                            Higher bids lead to higher quality.
                            Increased CPIs boost your game ranking and provide access to a higher quality user base.
                            Our algorithm suggests this optimal CPI based on the bids of other similar apps in the same category.
                          </Text>
                        )}
                        
                        {type === "Cap Suggestions" && (
                          <Text fontSize="sm" color="gray.600" mb={3}>
                            Increasing your budget allows for more ad delivery throughout the day.
                            This budget recommendation is based on your campaign's performance potential.
                          </Text>
                        )}
                        
                        <VStack spacing={2} align="stretch">
                          {Object.entries(group.campaigns).map(([campaignId, campaignSuggestions]) => (
                            <Box 
                              key={campaignId}
                              bg={bgColor}
                              p={2}
                              borderRadius="md"
                            >
                              <Text fontWeight="bold" fontSize="sm" mb={1}>
                                {campaignSuggestions[0].campaign.name}
                              </Text>
                              {campaignSuggestions.map((suggestion) => (
                                <SuggestionComparisonView
                                  key={suggestion.id}
                                  {...suggestion}
                                />
                              ))}
                            </Box>
                          ))}
                        </VStack>
                      </Box>
                    ))}
                  </VStack>
                )}
              </>
            )}
          </ModalBody>
          
          <ModalFooter justifyContent={"center"}>
            <Button 
              colorScheme="brand"
              isDisabled={suggestions.length === 0 || isApplying} 
              onClick={handleApplyAllSuggestions}
              leftIcon={<Icon as={IoSparkles} />}
            >
              Apply All
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      {ConfirmDialog}
    </>
  );
};

export default AllSuggestionsModal; 